import type {
  AuthUser,
  CreateProposalResponse,
  LinkLookupResponse,
  LinkResponse,
  MeResponse,
  MyProposalsResponse,
  ProposalSummaryResponse,
  SiteAnalyticsResponse,
  TrackBatch,
} from '@cloudbtl/shared';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

// ── 엔터프라이즈(자료실) 역할 축 두 개 — 섞지 말 것 (api/common/roles.ts 와 동일 모델) ──
/** 문서함(폴더) 역할: 특정 자료함에 대한 접근. */
export type FolderRole = 'ADMIN' | 'EDITOR' | 'VIEWER';
/** 워크스페이스 역할: 조직 내 지위. 폴더 생성·org 관리의 기준. */
export type OrgRole = 'OWNER' | 'ADMIN' | 'MEMBER';
export type TrialPlan = 'pro' | 'business';

export interface FolderMember {
  id: string;
  /** ProjectMember.email 은 nullable — userId 로만 붙은 레거시 행은 null 이 올 수 있다. */
  email: string | null;
  role: FolderRole;
  /** 가입 계정과 연결 안 된 이메일 초대 상태 (로그인 시 판정기가 리컨실). */
  pending: boolean;
  /** 연결된 계정의 이름. pending 이면 null. */
  name: string | null;
  createdAt: string;
}

export interface OrgMember {
  id: string;
  userId: string;
  email: string;
  name: string;
  role: OrgRole;
  createdAt: string;
}

export interface OrgInvitation {
  id: string;
  email: string;
  role: OrgRole;
  invitedByUserId: string | null;
  createdAt: string;
}

/** GET /orgs 한 행 — 내가 속한 워크스페이스 + 그 안에서의 내 역할. */
export interface MyOrg {
  id: string;
  subdomain: string;
  name: string;
  /** 현재 유효 티어. 활성 체험 중이면 trialPlan, 아니면 basePlan. */
  plan: string;
  /** 결제/수동 설정으로 보존되는 원래 티어. 체험 만료 시 이 값으로 자동 복귀한다. */
  basePlan: string;
  trialPlan: TrialPlan | null;
  trialStartedAt: string | null;
  trialEndsAt: string | null;
  trialActive: boolean;
  paypalStatus: string | null;
  paypalNextBillingTime: string | null;
  createdAt: string;
  role: OrgRole;
}

export interface TrustedIssuer {
  id: string;
  issuer: string;
  audience: string;
  jwksUrl: string | null;
  publicKeyPem: string | null;
  emailClaim: string;
  defaultRole: 'MEMBER' | 'ADMIN';
  disabled: boolean;
  identityCount: number;
  createdAt: string;
}

export interface TrustedIssuerInput {
  issuer: string;
  audience: string;
  jwksUrl?: string;
  publicKeyPem?: string;
  emailClaim?: string;
  defaultRole?: 'MEMBER' | 'ADMIN';
  disabled?: boolean;
}

export interface OrgProposal {
  id: string;
  title: string;
  kind: 'html' | 'md' | 'pdf' | 'pptx';
  fileSize: number;
  createdAt: string;
  /** null = 주인 없음(퇴사·계정삭제) → 링크 회수 대상. */
  owner: { id: string; email: string } | null;
  /** null = 미분류(어느 자료함에도 안 담김). */
  project: { id: string; code: string; name: string } | null;
  activeLinks: number;
}

export interface AuditEntry {
  id: string;
  actorUserId: string | null;
  actorEmail: string;
  action: string;
  targetType: string;
  targetId: string;
  detail: unknown;
  createdAt: string;
}

// ── Smartlake 타입 (서버 lake.service.ts / landing.controller.ts 응답 모양) ──
export type LakeDocType = 'pdf' | 'html' | 'md' | 'pptx' | 'docx' | 'xlsx' | 'file';

export interface LakeDocument {
  id: string;
  title: string;
  originalFilename: string;
  documentType: LakeDocType;
  mimeType: string;
  fileSize: number;
  createdAt: string;
  contentHash: string | null;
  source: string | null;
  sourceRef: string | null;
  ingestBatch: string | null;
  metadata: Record<string, unknown>;
  lineageId: string | null;
  version: number;
  supersedesId: string | null;
  storageMode?: 'stored' | 'linked';
  externalUri?: string | null;
  bytesPresent?: boolean;
  project: { id: string; code: string; name: string } | null;
  owner: { id: string; email: string } | null;
  baseline: {
    status: string;
    attempt: number;
    error: string | null;
    finishedAt: string | null;
    pageCount: number | null;
    charCount: number | null;
    hasTextLayer: boolean | null;
    reason: string | null;
    nextAttemptAt?: string | null;
    truncated?: boolean;
  } | null;
  descriptors: Array<{ kind: string; producer: string; count: number }>;
}

export interface LakeListParams {
  q?: string;
  source?: string;
  batch?: string;
  type?: string;
  lineage?: string;
  kind?: string;
  missingKind?: string;
  producer?: string;
  missingProducer?: string;
  baseline?: string;
  /** 트리 노드 id — 서브트리 문서만. */
  node?: string;
  limit?: number;
  cursor?: string;
}

export interface LakeStats {
  ok: true;
  total: number;
  bytes: number;
  storedDocs?: number;
  storedBytes?: number;
  byType: Record<string, { docs: number; bytes: number }>;
  bySource: Array<{ source: string | null; docs: number }>;
  batches: Array<{ ingestBatch: string | null; docs: number; bytes: number; lastAt: string | null; source: string | null }>;
  baseline: Record<string, number>;
  descriptors: Array<{ kind: string; producer: string; docs: number; rows: number }>;
}

export interface LakeTree {
  id: string;
  key: string;
  label: string;
  producer: string;
  producerVersion: string;
  rootId: string | null;
  nodes: number;
  documents: number;
}

export interface TreeOption {
  kind: 'node' | 'document' | 'page';
  id: string;
  label: string;
  weight: number;
  cards: Array<{ producer: string; producerVersion: string; payload: unknown }>;
  node?: { path: string; depth: number; docCount: number; docCountTotal: number; children: number };
  document?: { documentType: string; fileSize: number; createdAt: string; sourceRef: string | null };
  page?: number;
}

export interface TreeHop {
  ok: true;
  tree: { id: string; key: string; label: string };
  at: { kind: 'node'; id: string; label: string; path: string; depth: number } | { kind: 'document'; id: string; label: string; documentType: string };
  ancestors: Array<{ id: string; key: string; label: string; path: string; depth: number }>;
  card: Record<string, unknown> | null;
  options: TreeOption[];
  totals: { nodes: number; documents: number; pages?: number };
  nextOffset: number | null;
}

export interface LandResult {
  ok: boolean;
  file: string;
  deduplicated?: boolean;
  proposal?: { id: string; title: string; contentHash: string; version: number; lineageId: string | null; documentType: string; fileSize: number };
  baseline?: { status: string; jobId: string; pageCount?: number; charCount?: number; reason?: string; error?: string } | null;
  error?: string;
}

export interface LandResponse {
  ok: true;
  ingestBatch: string;
  counts: { files: number; landed: number; deduplicated: number; failed: number };
  results: LandResult[];
}

async function request<T>(path: string, init?: RequestInit & { isJson?: boolean }): Promise<T> {
  const response = await fetch(`/api${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init?.body && !(init.body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {}),
      ...(init?.headers ?? {}),
    },
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const message =
      (data && typeof data === 'object' && 'error' in data && (data as { error?: string }).error) ||
      response.statusText;
    const err = new ApiError(response.status, message);
    (err as unknown as { payload?: unknown }).payload = data;
    throw err;
  }
  return data as T;
}

export interface DocumentSearchResult {
  documentId: string; descriptorId: string; title: string; sourceRef: string | null;
  contentHash: string | null; page: number; kind: string; producer: string; snippet: string;
}
export interface DocumentSearchResponse {
  searchId: string; query: string; results: DocumentSearchResult[]; nextCursor: string | null;
}

export const api = {
  searchDocuments: (q: string, cursor?: string) => request<DocumentSearchResponse>(`/search?${new URLSearchParams({ q, ...(cursor ? { cursor } : {}) })}`),
  readSearchResult: (r: DocumentSearchResult, searchId: string) => request<{ text: string; truncated: boolean }>(`/search/${encodeURIComponent(r.documentId)}/${encodeURIComponent(r.descriptorId)}?searchId=${encodeURIComponent(searchId)}`),
  searchFeedback: (body: { searchId: string; requestId: string; question: string; note: string; documentIds: string[]; shared: boolean }) => request<{ id: string; shared: boolean }>('/search/feedback', { method: 'POST', body: JSON.stringify(body) }),
  me: () => request<MeResponse>('/me'),
  signup: (email: string, password: string, name?: string) =>
    request<{ ok: true; user: AuthUser; verificationUrl?: string }>('/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password, name }),
    }),
  login: (email: string, password: string) =>
    request<{ ok: true; user: AuthUser }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  googleLogin: (credential: string) =>
    request<{ ok: true; user: AuthUser }>('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ credential }),
    }),
  issueAuthHandoff: (returnUrl: string) =>
    request<{ ok: true; code: string; returnUrl: string }>('/auth/handoff', {
      method: 'POST',
      body: JSON.stringify({ returnUrl }),
    }),
  consumeAuthHandoff: (code: string) =>
    request<{ ok: true }>('/auth/handoff/consume', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),
  requestVerification: () =>
    request<{ ok: true; alreadyVerified?: boolean; verificationUrl?: string }>(
      '/auth/verify-email/request',
      {
        method: 'POST',
      },
    ),
  confirmVerification: (token: string) =>
    request<{ ok: true; user: AuthUser }>('/auth/verify-email/confirm', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  requestPasswordReset: (email: string) =>
    request<{ ok: true; message: string; resetUrl?: string }>('/auth/password-reset/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  confirmPasswordReset: (token: string, password: string) =>
    request<{ ok: true; user: AuthUser }>('/auth/password-reset/confirm', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),
  myProposals: () => request<MyProposalsResponse>('/me/proposals'),
  uploadProposal: (form: FormData) =>
    request<CreateProposalResponse>('/proposals', { method: 'POST', body: form }),
  lookupLink: (linkId: string, accessToken?: string) => {
    const query = accessToken ? `?accessToken=${encodeURIComponent(accessToken)}` : '';
    return request<LinkLookupResponse>(`/links/${linkId}${query}`);
  },
  resolveAccess: (linkId: string, body: { passcode?: string; credential?: string }) =>
    request<{ ok: true; accessToken: string; proposal: unknown }>(`/links/${linkId}/access`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateProposal: (proposalId: string, ownerKey: string | null, title: string) => {
    const query = ownerKey ? `?key=${encodeURIComponent(ownerKey)}` : '';
    return request<{ ok: true; proposal: { id: string; title: string } }>(
      `/proposals/${proposalId}${query}`,
      { method: 'PATCH', body: JSON.stringify({ title }) },
    );
  },
  deleteProposal: (proposalId: string, ownerKey: string | null) => {
    const query = ownerKey ? `?key=${encodeURIComponent(ownerKey)}` : '';
    return request<{ ok: true }>(`/proposals/${proposalId}${query}`, { method: 'DELETE' });
  },
  getProposalSource: (proposalId: string, ownerKey?: string | null) => {
    const query = ownerKey ? `?key=${encodeURIComponent(ownerKey)}` : '';
    return request<{ ok: true; html: string; documentType: string; title: string }>(
      `/proposals/${proposalId}/source${query}`,
    );
  },
  updateProposalSource: (proposalId: string, ownerKey: string | null, html: string) => {
    const query = ownerKey ? `?key=${encodeURIComponent(ownerKey)}` : '';
    return request<{ ok: true; fileSize: number; id: string; contentHash: string | null; version: number; changed: boolean }>(`/proposals/${proposalId}/source${query}`, {
      method: 'PUT',
      body: JSON.stringify({ html }),
    });
  },
  updateProfile: (name: string) =>
    request<{ ok: true; user: AuthUser }>('/me', {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true }>('/me/password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
  deleteAccount: (password: string) =>
    request<{ ok: true }>('/me', {
      method: 'DELETE',
      body: JSON.stringify({ password }),
    }),
  // ownerKey 는 익명 업로더(개인 문서)의 관리 축. org 문서는 서버가 ownerKey 를 인정하지 않고
  // 세션 소유권/org ADMIN 으로만 판정하므로, 테넌트 화면은 null 로 호출해 ?key= 를 아예 붙이지 않는다.
  createLink: (proposalId: string, ownerKey: string | null, body: Record<string, unknown>) => {
    const query = ownerKey ? `?key=${encodeURIComponent(ownerKey)}` : '';
    return request<LinkResponse>(`/proposals/${proposalId}/links${query}`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  updateLink: (
    proposalId: string,
    linkId: string,
    ownerKey: string,
    body: Record<string, unknown>,
  ) =>
    request<LinkResponse>(
      `/proposals/${proposalId}/links/${linkId}?key=${encodeURIComponent(ownerKey)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  deleteLink: (proposalId: string, linkId: string, ownerKey: string) =>
    request<{ ok: true }>(
      `/proposals/${proposalId}/links/${linkId}?key=${encodeURIComponent(ownerKey)}`,
      { method: 'DELETE' },
    ),
  proposalSummary: (proposalId: string, ownerKey?: string | null) => {
    const query = ownerKey ? `?key=${encodeURIComponent(ownerKey)}` : '';
    return request<ProposalSummaryResponse>(`/proposals/${proposalId}/summary${query}`);
  },
  siteAnalytics: (days: number) =>
    request<SiteAnalyticsResponse>(`/site-analytics/summary?days=${days}`),
  postEvents: (batch: TrackBatch) =>
    request<{ ok: true; accepted: number }>('/events', {
      method: 'POST',
      body: JSON.stringify(batch),
    }),
  postSiteEvents: (events: unknown[]) =>
    request<{ ok: true; accepted: number; identified: boolean }>('/site-events', {
      method: 'POST',
      body: JSON.stringify({ events }),
    }),
  // ── 리퍼럴 어트리뷰션 (powered-by 배지 → 랜딩 → 가입) ──
  // 전부 best-effort: 호출부는 실패를 삼켜 페이지/가입 UX 를 절대 깨지 않는다.
  recordPoweredByClick: (proposalId: string, agencyId: string, visitorId: string) =>
    request<{ ok: true; accepted: number }>('/attribution/powered-by-click', {
      method: 'POST',
      body: JSON.stringify({ proposalId, agencyId, visitorId }),
    }),
  issueReferralToken: (agencyId: string, proposalId: string, visitorId: string) =>
    request<{ ok: true; token: string }>('/attribution/referral-token', {
      method: 'POST',
      body: JSON.stringify({ agencyId, proposalId, visitorId }),
    }),
  attributeSignup: (referralToken: string) =>
    request<{ ok: true; proposalId: string; agencyId: string }>('/attribution/attribute-signup', {
      method: 'POST',
      body: JSON.stringify({ referralToken }),
    }),
  // 엔터프라이즈 문서함(자료실) — 테넌트 서브도메인에서만 유효.
  // ★멤버가 아니면(비로그인 포함) subdomain 만 온다 — plan·name·customDomain·orgId 는
  //   고객 명단 열거 표면이라 서버가 멤버에게만 준다. 소비처는 부재를 전제로 fallback 할 것.
  currentOrg: () =>
    request<{
      ok: true;
      tenant: {
        subdomain: string;
        orgId?: string;
        /** 커스텀 도메인 별칭(있으면 발행 링크가 이 도메인). */
        customDomain?: string | null;
        plan?: string;
        name?: string;
      } | null;
    }>('/orgs/current'),
  projects: () =>
    request<{
      ok: true;
      projects: Array<{
        id: string;
        code: string;
        name: string;
        createdAt: string;
        // externalRef 계열은 EDITOR+ 에게만 채워진다(VIEWER 는 항상 null).
        externalRef: string | null;
        externalRefKind: string | null;
        visibility: 'org' | 'private';
        myRole: FolderRole;
        docCount: number;
      }>;
    }>('/projects'),
  createProject: (
    code: string,
    name?: string,
    ref?: { externalRef?: string; externalRefKind?: string },
  ) =>
    request<{ ok: true; project: { id: string; code: string; name: string } }>('/projects', {
      method: 'POST',
      body: JSON.stringify({ code, name, ...ref }),
    }),
  // externalRef/externalRefKind 는 쌍으로만 (빈 문자열 쌍 = 해제). 한쪽만 주면 400.
  updateProject: (
    id: string,
    patch: {
      name?: string;
      visibility?: 'org' | 'private';
      externalRef?: string;
      externalRefKind?: string;
    },
  ) =>
    request<{
      ok: true;
      project: {
        id: string;
        code: string;
        name: string;
        visibility: string;
        externalRef: string | null;
        externalRefKind: string | null;
      };
    }>(`/projects/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  deleteProject: (id: string) =>
    request<{ ok: true; deleted: string }>(`/projects/${id}`, { method: 'DELETE' }),
  folderDocs: (projectId: string) =>
    request<{
      ok: true;
      proposals: Array<{
        id: string;
        title: string;
        kind: 'html' | 'md' | 'pdf' | 'pptx';
        fileSize: number;
        createdAt: string;
        /** 대표 공유링크 URL(활성 1건). linkMode:'none' 업로드면 null. */
        shareUrl: string | null;
        /** 내가 올린 문서인지 — 편집·삭제 가능 여부 힌트(최종 판정은 서버). */
      }>;
    }>(`/projects/${projectId}/proposals`),
  // 문서 내용 서빙 URL(브라우저가 쿠키로 직접 fetch/iframe). VIEWER+.
  folderDocContentUrl: (projectId: string, pid: string) =>
    `/api/projects/${projectId}/proposals/${pid}/content`,

  // ── 자료함 ACL (문서함 축) — 전부 문서함 ADMIN 필요. 비ADMIN 은 403. ──
  folderMembers: (projectId: string) =>
    request<{ ok: true; members: FolderMember[] }>(`/projects/${projectId}/members`),
  /** 미가입 이메일도 허용(pending) — 그 이메일로 로그인하면 판정기가 리컨실. */
  addFolderMember: (projectId: string, email: string, role: FolderRole) =>
    request<{
      ok: true;
      member: { id: string; email: string; role: FolderRole; pending: boolean };
    }>(`/projects/${projectId}/members`, { method: 'POST', body: JSON.stringify({ email, role }) }),
  updateFolderMember: (projectId: string, memberId: string, role: FolderRole) =>
    request<{ ok: true; role: FolderRole }>(`/projects/${projectId}/members/${memberId}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }),
  removeFolderMember: (projectId: string, memberId: string) =>
    request<{ ok: true }>(`/projects/${projectId}/members/${memberId}`, { method: 'DELETE' }),

  // ── 워크스페이스(org) 생성·목록 — 루트 도메인에서도 유효(테넌트 무관), 로그인 필수 ──
  /** 내가 속한 워크스페이스 전부. 없으면 빈 배열 — 그때가 생성 폼을 보여줄 유일한 상태. */
  myOrgs: () => request<{ ok: true; orgs: MyOrg[] }>('/orgs'),
  /** 서브도메인 규칙·예약어·중복·플랜 보유상한은 전부 서버가 판정 — 에러 문구를 그대로 노출할 것.
   *  ★이메일 미검증 계정은 403 (서브도메인 = 영구 선점 자산이라 서버가 막는다). */
  createOrg: (subdomain: string, name?: string) =>
    request<{
      ok: true;
      org: { id: string; subdomain: string; name: string; plan: string; createdAt: string };
    }>('/orgs', { method: 'POST', body: JSON.stringify({ subdomain, name }) }),
  /** OWNER + 인증 이메일만, 워크스페이스당 1회. 결제수단 없이 30일 뒤 basePlan 으로 자동 복귀. */
  startOrgTrial: (orgId: string, plan: TrialPlan) =>
    request<{ ok: true; org: Omit<MyOrg, 'role'> }>(`/orgs/${orgId}/trial`, {
      method: 'POST',
      body: JSON.stringify({ plan }),
    }),
  /** OWNER + 인증 이메일. 서버가 PayPal 구독을 만들고 PayPal 승인 URL만 반환한다. */
  createPayPalSubscription: (orgId: string, plan: TrialPlan) =>
    request<{
      ok: true;
      subscriptionId: string;
      approvalUrl: string;
      status: string;
      reused: boolean;
    }>(`/orgs/${orgId}/billing/paypal/subscriptions`, {
      method: 'POST',
      body: JSON.stringify({ plan }),
    }),
  /** PayPal 복귀 파라미터를 신뢰하지 않고 서버가 PayPal API에서 ACTIVE 상태를 재검증한다. */
  confirmPayPalSubscription: (orgId: string, subscriptionId: string) =>
    request<{
      ok: true;
      subscription: { plan: TrialPlan; status: string };
    }>(`/orgs/${orgId}/billing/paypal/confirm`, {
      method: 'POST',
      body: JSON.stringify({ subscriptionId }),
    }),

  // ── 워크스페이스(org) 관리 — 전부 org ADMIN 이상. orgId 는 currentOrg().tenant.orgId. ──
  orgMembers: (orgId: string) =>
    request<{ ok: true; members: OrgMember[] }>(`/orgs/${orgId}/members`),
  /** OWNER 승격·강등은 OWNER 만, 마지막 OWNER 강등은 400. */
  updateOrgMember: (orgId: string, memberId: string, role: OrgRole) =>
    request<{ ok: true; role: OrgRole }>(`/orgs/${orgId}/members/${memberId}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }),
  removeOrgMember: (orgId: string, memberId: string) =>
    request<{ ok: true }>(`/orgs/${orgId}/members/${memberId}`, { method: 'DELETE' }),
  /** pending 초대만 (revoked/accepted 제외). */
  orgInvitations: (orgId: string) =>
    request<{ ok: true; invitations: OrgInvitation[] }>(`/orgs/${orgId}/invitations`),
  /** 이미 멤버인 이메일은 400 — 역할 변경은 updateOrgMember 로. 자기보다 높은 role 부여는 403. */
  inviteOrgMember: (orgId: string, email: string, role: OrgRole) =>
    request<{
      ok: true;
      invitation: { id: string; email: string; role: OrgRole; createdAt: string };
    }>(`/orgs/${orgId}/invitations`, { method: 'POST', body: JSON.stringify({ email, role }) }),
  /** 이미 수락된 초대면 409 — 그땐 removeOrgMember 로 안내해야 한다. */
  revokeOrgInvitation: (orgId: string, invId: string) =>
    request<{ ok: true }>(`/orgs/${orgId}/invitations/${invId}`, { method: 'DELETE' }),
  /** org 전체 문서 — 폴더에 안 담긴·주인 없는 문서를 찾는 유일한 경로(링크 회수의 전제). */
  orgProposals: (orgId: string) =>
    request<{ ok: true; proposals: OrgProposal[] }>(`/orgs/${orgId}/proposals`),
  /** 관리행위 감사 로그, 최신순. limit 1~500 클램프(기본 100). */
  orgAudit: (orgId: string, limit?: number) => {
    const query = limit ? `?limit=${encodeURIComponent(String(limit))}` : '';
    return request<{ ok: true; entries: AuditEntry[] }>(`/orgs/${orgId}/audit${query}`);
  },
  // Enterprise SSO — OWNER만. 생성·편집·활성화는 Business/활성 Business 체험 필요.
  ssoIssuers: (orgId: string) =>
    request<{ ok: true; entitled: boolean; issuers: TrustedIssuer[] }>(
      `/orgs/${orgId}/sso/issuers`,
    ),
  createSsoIssuer: (orgId: string, body: TrustedIssuerInput) =>
    request<{ ok: true; issuer: Omit<TrustedIssuer, 'identityCount'> }>(
      `/orgs/${orgId}/sso/issuers`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    ),
  updateSsoIssuer: (orgId: string, issuerId: string, body: Partial<TrustedIssuerInput>) =>
    request<{ ok: true; issuer: Omit<TrustedIssuer, 'identityCount'> }>(
      `/orgs/${orgId}/sso/issuers/${issuerId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(body),
      },
    ),
  deleteSsoIssuer: (orgId: string, issuerId: string) =>
    request<{ ok: true; deleted: string; detachedIdentities: number }>(
      `/orgs/${orgId}/sso/issuers/${issuerId}`,
      { method: 'DELETE' },
    ),
  // ── Smartlake — 뭐든 던져 넣는 원천층 + 훑어보기. 로그인 필수. 테넌트면 멤버, 아니면 개인 문서. ──
  /** POST /documents/land — multipart. 파일 파트 이름 자유. ≤50 파일, 요청 본문 실효 한도 ~30MB(프록시). */
  landDocuments: (form: FormData) =>
    request<LandResponse>('/documents/land', { method: 'POST', body: form }),
  lakeDocuments: (params: LakeListParams) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    const q = qs.toString();
    return request<{ ok: true; documents: LakeDocument[]; nextCursor: string | null }>(`/documents${q ? '?' + q : ''}`);
  },
  lakeStats: () => request<LakeStats>('/documents/stats'),
  /** 업로드 전 건너뛰기용 — 스코프 안에 이미 있는 해시. */
  lakeExists: (hashes: string[]) =>
    request<{ ok: true; existing: Record<string, { id: string; title: string; createdAt: string }> }>('/documents/exists', {
      method: 'POST',
      body: JSON.stringify({ hashes }),
    }),
  docDescriptorSummary: (id: string) =>
    request<{ ok: true; summary: Array<{ kind: string; producer: string; producerVersion: string; count: number; updatedAt: string }> }>(
      `/proposals/${id}/descriptors/summary`,
    ),
  docDescriptors: (id: string, params: { kind?: string; producer?: string; page?: number }) => {
    const qs = new URLSearchParams();
    if (params.kind) qs.set('kind', params.kind);
    if (params.producer) qs.set('producer', params.producer);
    if (params.page !== undefined) qs.set('page', String(params.page));
    return request<{ ok: true; descriptors: Array<{ id: string; kind: string; producer: string; producerVersion: string; page: number; payload: unknown; updatedAt: string }> }>(
      `/proposals/${id}/descriptors?${qs.toString()}`,
    );
  },
  docJobs: (id: string) =>
    request<{ ok: true; jobs: Array<{ id: string; kind: string; status: string; attempt: number; worker: string | null; workerVersion: string | null; error: string | null; detail: unknown; queuedAt: string; finishedAt: string | null }> }>(
      `/proposals/${id}/jobs`,
    ),
  rerunBaseline: (id: string) =>
    request<{ ok: true; status: string; jobId: string }>(`/proposals/${id}/jobs/baseline/run`, { method: 'POST' }),
  /** queued 기본추출 처리 — 테넌트면 org ADMIN+, 아니면 개인. */
  drainPipeline: (limit: number) =>
    request<{ ok: true; processed: number; [k: string]: unknown }>(`/pipeline/drain?limit=${encodeURIComponent(String(limit))}`, { method: 'POST' }),
  /** 대용량 직접 업로드: init(티켓+GCS 세션 URL) → 브라우저가 PUT → commit(해시·dedupe·행·baseline). */
  landInit: (filename: string, size: number) =>
    request<{ ok: true; uploadId: string; uploadUrl: string; expiresAt: string; maxBytes: number }>('/documents/land/init', {
      method: 'POST',
      body: JSON.stringify({ filename, size }),
    }),
  landCommit: (body: { uploadId: string; source?: string; sourceRef?: string; metadata?: Record<string, unknown>; ingestBatch?: string; runBaseline?: boolean }) =>
    request<LandResult & { ok: true }>('/documents/land/commit', {
      method: 'POST',
      body: JSON.stringify({ ...body, linkMode: 'none', dedupe: true }),
    }),
  rawDocUrl: (id: string) => `/api/proposals/${id}/raw`,
  // ── 트리·선택지 (spec 1.2) ──
  trees: () => request<{ ok: true; trees: LakeTree[] }>('/trees'),
  treeNode: (tree: string, node: string, limit = 50, offset = 0) =>
    request<TreeHop>(`/trees/${encodeURIComponent(tree)}/nodes/${encodeURIComponent(node)}?limit=${limit}&offset=${offset}`),
  rebuildFoldersTree: () => request<{ ok: true; documents: number; attached: number }>('/trees/folders/rebuild', { method: 'POST' }),
  backfillCards: (limit: number) =>
    request<{ ok: true; processed: number }>(`/pipeline/cards/backfill?limit=${encodeURIComponent(String(limit))}`, { method: 'POST' }),
};
